/**
 * Button / IconButton / ButtonGroup — DS-04, DS-44, DS-45, DS-48.
 *
 * Padding matches the `.btn` helper in index.css on purpose: hand-written
 * toolbar markup and <Button> have to be the same height on the same row.
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Spinner } from './Spinner';
import { Tooltip, type TooltipSide } from './Tooltip';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'subtle';
type ButtonSize = 'sm' | 'md' | 'icon';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded font-bold transition disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
        // DS-44 — the secondary button is white with a slate-300 frame, never a tinted fill.
        secondary: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100',
        danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
        ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-800',
        subtle: 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300',
      },
      // NFR-05 — the `max-lg:` half is the phone and the tablet in portrait.
      // It used to lift every button to DS-54's 44px and one step of type; on a
      // 375px screen that is what pushed a header's action row off the edge, so
      // the mobile floor is 36px at the desktop type size — a thumb still lands
      // on it, and three buttons still fit across a phone. Above 1024px not one
      // of these rules is emitted, so the desktop button is the size it always
      // was.
      size: {
        sm: 'px-2 py-1 text-[11px] max-lg:min-h-8 max-lg:px-2.5',
        md: 'px-2.5 py-1.5 text-xs max-lg:min-h-9',
        icon: 'p-1.5 text-xs max-lg:min-h-9 max-lg:min-w-9',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the leading icon for a spinner and blocks the click. */
  loading?: boolean;
  icon?: LucideIcon;
  iconEnd?: LucideIcon;
  fullWidth?: boolean;
  /** DS-50 — set on chevrons and arrows so they point with the reading direction. */
  flipIcon?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon: Icon,
    iconEnd: IconEnd,
    fullWidth = false,
    flipIcon = false,
    type = 'button',
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  // DS-45 — Lucide only, w-4 h-4 inside a button, whatever the button size.
  const iconClass = cn('h-4 w-4 shrink-0', flipIcon && 'flip-rtl');

  return (
    <button
      ref={ref}
      // Defaults to `button` so a toolbar control inside a <form> never submits it
      // by accident; submit buttons opt in explicitly.
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth }), className)}
      {...rest}
    >
      {loading ? <Spinner size="md" /> : Icon ? <Icon className={iconClass} aria-hidden="true" /> : null}
      {children}
      {IconEnd && !loading ? <IconEnd className={iconClass} aria-hidden="true" /> : null}
    </button>
  );
});

interface IconButtonProps
  extends Omit<ButtonProps, 'icon' | 'iconEnd' | 'size' | 'fullWidth' | 'children' | 'title'> {
  icon: LucideIcon;
  /**
   * Arabic label. It is the tooltip *and* the accessible name — an icon-only
   * control with no name is unusable for half the staff.
   */
  title: string;
  size?: 'sm' | 'md';
  /** Longer copy for the bubble, when the accessible name is too terse to explain the click. */
  tooltip?: ReactNode;
  tooltipSide?: TooltipSide;
  /** Renders the button bare — for a control whose parent already owns the bubble. */
  noTooltip?: boolean;
}

/**
 * DS-04 — the label is spoken by the bubble, never by the browser's own tooltip.
 * The native `title` attribute is the wrong instrument for a row of action
 * icons: it waits a second or so, it lands wherever the pointer happens to be
 * rather than against the button, it renders in the OS font at the OS size, and
 * on an RTL page it is laid out by the OS locale instead of ours. A table's
 * action column is exactly where those four cost the most, so IconButton always
 * carries a <Tooltip>. `aria-label` still holds the name — the half a screen
 * reader reads.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon,
    title,
    size = 'md',
    variant = 'ghost',
    tooltip,
    tooltipSide = 'top',
    noTooltip = false,
    className,
    ...rest
  },
  ref,
) {
  return (
    <Tooltip content={tooltip ?? title} side={tooltipSide} disabled={noTooltip}>
      <Button
        ref={ref}
        variant={variant}
        size="icon"
        icon={icon}
        aria-label={title}
        className={cn(size === 'sm' && 'p-1', className)}
        {...rest}
      />
    </Tooltip>
  );
});

interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** Arabic name of the group, announced instead of reading every segment. */
  label?: string;
}

/**
 * Segmented control: the children keep only the outer corners and overlap by one
 * pixel so the shared borders collapse into a single line. All of it is logical,
 * so the first child rounds on the right in Arabic and on the left in English.
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(function ButtonGroup(
  { label, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={cn(
        'inline-flex items-stretch',
        '[&>*]:rounded-none [&>*:first-child]:rounded-s [&>*:last-child]:rounded-e',
        '[&>*+*]:-ms-px',
        // Lift the hovered/focused segment so its full border is drawn, not clipped
        // by the neighbour that overlaps it.
        '[&>*:focus-visible]:relative [&>*:focus-visible]:z-10 [&>*:hover]:relative [&>*:hover]:z-10',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
